import { skipToken } from '@reduxjs/toolkit/query'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { BackButton } from '../../components/button/backButton'
import CountCard from '../../components/cards/CountCard'
import Icon from '../../components/icon'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Spinner from '../../components/spinner/Spinner'
import { TableComponent } from '../../components/table'
import { MiniHeader } from '../../components/typography/miniHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import {
  formatCurrency,
  formatDate,
  getPageFromSkip,
  getSkipFromPage,
} from '../../helpers'
import Layout from '../../layout'
import { useGetSingleSalesRepReportQuery } from '../../redux-toolkits/reports/reports.slice'
import {
  ReportPeriodRange,
  SingleSalesRepReportQuery,
} from '../../redux-toolkits/reports/reports.type'

export const SingleSalesRepPage: React.FC = () => {
  const [query, setQuery] = useState<SingleSalesRepReportQuery>()

  const { id } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()

  const navigate = useNavigate()

  const {
    data: salesRep,
    isFetching,
    isLoading,
  } = useGetSingleSalesRepReportQuery(
    query
      ? {
          ...query,
          id: id ?? '',
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 20,
      //   search: searchParams.get('search') || undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
    })
  }, [searchParams])

  return (
    <Layout>
      {isLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <div className="flex flex-col gap-10">
          <BackButton
            onClick={() => {
              navigate('/dashboard')
            }}
          />

          <div className="flex flex-col gap-2 text-sec-black">
            <div
              className="
                w-[80px] h-[80px]
                rounded-full bg-[#4F122A]
                flex items-center justify-center
                text-lg text-white font-medium
                "
            >
              {salesRep?.data?.salesRep?.name?.charAt(0)}
            </div>
            <p className="text-[40px] text-black font-recoleta font-semibold capitalize">
              {salesRep?.data?.salesRep?.name} (Sales Rep)
            </p>
            <div className="flex items-center gap-2">
              <Icon id="envelope" width={24} height={24} />
              <p>{salesRep?.data?.salesRep?.email}</p>
            </div>
          </div>

          <DateFilterNav
            filter={query?.filter ?? 'custom'}
            isCustomDateRange={query?.isCustomDateRange ?? false}
            startDate={query?.startDate}
            endDate={query?.endDate}
            setFilter={(filter) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                filter: filter.filter ?? '',
                isCustomDateRange: String(filter.isCustomDateRange),
                endDate: filter.endDate ?? '',
                startDate: filter.startDate ?? '',
              })
            }}
            className="w-fit"
          />

          <CountCardContainer className="!my-0">
            <CountCard
              count={salesRep?.data.summary.totalOrderAmount ?? 0}
              text="Total order amount"
              isCurrency={true}
            />
            <CountCard
              count={salesRep?.data.summary.totalOrderCount ?? 0}
              text="Total order count"
              isCurrency={false}
            />
          </CountCardContainer>

          <MiniHeader name="Sales History" />

          <div className="w-full overflow-x-scroll">
            <TableComponent
              headers={[
                'DATE',
                'TOTAL ORDER COUNT',
                'TOTAL ORDER AMOUNT',
                'CREATOR',
              ]}
              rows={
                salesRep?.data?.records?.map((record, idx) => {
                  return {
                    id: `${record.date}-${idx}`,
                    content: [
                      formatDate(record.date) ?? 'N/A',
                      record?.totalOrderCount?.toLocaleString() ?? 'N/A',
                      formatCurrency(record.totalOrderAmount ?? 0),
                      record.creator.name,
                    ],
                  }
                }) ?? []
              }
              name="sales-rep-sales-history"
              loading={isFetching}
              isEmpty={!salesRep?.data || salesRep.data.records.length < 1}
              totalDataCount={salesRep?.data.count}
              currentPage={getPageFromSkip(
                query?.skip ?? 0,
                query?.limit ?? 20,
              )}
              pageLimit={Number(query?.limit ?? 0)}
              onPageChange={(page) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  skip: `${getSkipFromPage(page, query?.limit ?? 20)}`,
                })
              }}
            />
          </div>
        </div>
      )}
    </Layout>
  )
}
